import ValidatedForm, { ValidatedTextarea, ValidatedSelect } from "../../ValidatedForm";
import { PaginatedList } from "./Pagination";
import { useEffect, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";
import { formatDateTime } from "./shared";

function commentRequestId() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default function AssignmentComments({ assignmentId, classroomId, profile, target }) {
  const root = useRef(null);
  const requestKeys = useRef({});
  const focusedTarget = useRef(null);
  const [comments, setComments] = useState([]);
  const [roster, setRoster] = useState([]);
  const [recipient, setRecipient] = useState("");
  const [drafts, setDrafts] = useState({ class: "", private: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const teacher = profile?.role === "teacher";
  useEffect(() => {
    setRecipient(""); setDrafts({ class: "", private: "" });
  }, [assignmentId, classroomId, profile?.id]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setComments([]);
    async function load() {
      try {
        const results = await Promise.all([
          supabase.rpc("list_assignment_comments", { requested_assignment_id: String(assignmentId), requested_classroom_id: String(classroomId) }),
          teacher ? supabase.rpc("get_classroom_roster", { requested_classroom_id: String(classroomId) }) : Promise.resolve({ data: [] }),
        ]);
        for (const result of results) if (result.error) throw result.error;
        if (!cancelled) { setComments(results[0].data || []); setRoster(results[1].data || []); }
      } catch (error) { if (!cancelled) setError(error.message || "Unable to load comments."); }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [assignmentId, classroomId, teacher, attempt]);

  useEffect(() => {
    if (loading || !target || focusedTarget.current === target || String(target.assignmentId) !== String(assignmentId)) return;
    focusedTarget.current = target;
    if (target.kind === "private" && teacher && (roster.some(r => r.student_id === target.studentId) || comments.some(c => c.student_id === target.studentId))) setRecipient(target.studentId);
    const section = root.current?.querySelector(`[data-comment-kind="${target.kind}"]`);
    section?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    section?.focus({ preventScroll: true });
  }, [target, loading, assignmentId, teacher, roster, comments]);

  async function submit(event, kind) {
    event.preventDefault();
    if (saving || (teacher && !recipient)) return;
    setSaving(kind); setError("");
    try {
      const signature = JSON.stringify([assignmentId, classroomId, kind, recipient, drafts[kind].trim()]);
      if (requestKeys.current[kind]?.signature !== signature) requestKeys.current[kind] = { signature, id: commentRequestId() };
      const { error } = await supabase.rpc("post_assignment_comment", {
        requested_assignment_id: String(assignmentId), requested_classroom_id: String(classroomId),
        comment_text: drafts[kind].trim(), comment_kind: kind,
        recipient_student_id: teacher ? recipient : null,
        request_id: requestKeys.current[kind].id,
      });
      if (error) throw error;
      delete requestKeys.current[kind];
      setDrafts((current) => ({ ...current, [kind]: "" }));
      setAttempt((value) => value + 1);
    } catch (error) { setError(error.message || "Unable to post comment."); }
    finally { setSaving(""); }
  }
  // Include historical conversation names, even when a student has since left.
  const recipients = new Map(roster.map((row) => [row.student_id, row.student_name]));
  comments.filter((row) => row.visibility === "private").forEach((row) => {
    if (!recipients.has(row.student_id)) recipients.set(row.student_id, row.recipient_name || "Student");
  });
  return <div ref={root} aria-label="Assignment comments" className="mt-5 space-y-5">
    {error && <p role="alert" className="text-sm text-red-700">{error} <button type="button" onClick={() => setAttempt((value) => value + 1)} className="underline">Retry</button></p>}
    {["class", "private"].map((kind) => {
      const title = kind === "class" ? "Class Comments" : "Private Comments";
      const rows = comments.filter((row) => (row.visibility || "private") === kind &&
        (kind === "class" || !teacher || row.student_id === recipient));
      const canPost = profile?.role === "student" || (teacher && kind === "private" && roster.some((r) => r.student_id === recipient));
      return <section key={kind} data-comment-kind={kind} tabIndex={-1} aria-label={title} className="space-y-4 rounded-xl border border-[#dadce0] bg-white p-5">
        <h3 className="font-medium text-[#202124]">{title}</h3>
        <p className="text-xs text-gray-500">{kind === "class" ? "Visible to enrolled classmates and the classroom teacher." : "Visible only to the student in this conversation and the classroom teacher."}</p>
        {teacher && kind === "private" && <label className="block text-sm">Student conversation
          <ValidatedSelect value={recipient} onChange={(event) => { setRecipient(event.target.value); setDrafts((d) => ({ ...d, private: "" })); }} className="ml-2 rounded-lg border p-2">
            <option value="">Select a student</option>
            {[...recipients].map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </ValidatedSelect>
        </label>}
        {loading ? <p role="status" className="text-sm text-gray-500">Loading comments...</p> :
          !error && (teacher && kind === "private" && !recipient ? <p className="text-sm text-gray-500">Select a student to view or reply privately.</p> :
          rows.length === 0 ? <p className="text-sm text-gray-500">No comments yet.</p> :
          <PaginatedList items={rows} resetKey={[assignmentId, classroomId, recipient, kind]} label="Comments">{pageItems => <ul className="divide-y divide-gray-100">{pageItems.map((comment) => <li key={comment.id} className="py-3">
            <p className="text-sm font-medium">{comment.sender_name || comment.student_name}</p>
            <time className="text-xs text-gray-500" dateTime={comment.created_at}>{formatDateTime(comment.created_at)}</time>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm">{comment.comment_text}</p>
          </li>)}</ul>}</PaginatedList>)}
        {canPost && <ValidatedForm onSubmit={(event) => submit(event, kind)} className="space-y-3">
          <label className="block text-sm">{kind === "class" ? "Class Comment" : "Private Comment"}
            <ValidatedTextarea value={drafts[kind]} onChange={(event) => setDrafts((current) => ({ ...current, [kind]: event.target.value }))}
              required maxLength={2000} rows={3} className="mt-1 block w-full rounded-lg border border-[#dadce0] p-3 text-sm focus:border-[#137333]" />
          </label>
          <button type="submit" disabled={Boolean(saving) || loading} className="rounded-full bg-[#137333] px-4 py-2 text-sm text-white disabled:opacity-50">
            {saving === kind ? "Posting..." : kind === "class" ? "Post class comment" : teacher ? "Reply privately" : "Post private comment"}
          </button>
        </ValidatedForm>}
      </section>;
    })}
  </div>;
}
